/**
 * Подраздел «Инвестиционные проекты» раздела «Проекты» (US-05.2):
 * реестр-аккордеон (FR-2.1) — строка = инвестпроект, в шапке название;
 * раскрытие показывает параметры (год · мощность · сумма инвестиций млн ₸ ·
 * рабочие места · описание · эффект, FR-2.2), дополнительные поля (FR-2.3)
 * и галерею фотографий (FR-2.4). Плашки нет — как реализовано сейчас
 * (открытый вопрос №2 постановки, владелец: PO).
 *
 * Источник данных — гибрид (как US-05.1): любой областной КАТО
 * (isProjectsDemoKato, включая пилотный ЗКО) → мок
 * hardData/projectsInvestment.json; остальные КАТО — запрос
 * /api/public/projects/investment (данные вносятся вручную в «Админке»,
 * US-03). Эндпоинт на бэке ещё не реализован: до его появления запрос
 * падает → пустой реестр с подписью, без ошибки (FR-2.5).
 *
 * Незаполненный параметр не показывается (FR-2.6); текстовые значения —
 * из языковой версии данных по языку интерфейса (FR-8.1).
 */
import { Landmark } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmt } from './format';
import { PhotoGallery } from './PhotoGallery';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import { Card, CardContent } from '../../../components/ui/card';
import { useKato } from '../../../contexts/KatoContext';
import { isProjectsDemoKato } from '../../../hardData/constants';
import projectsInvestment from '../../../hardData/projectsInvestment.json';
import { getProjectsInvestment } from '../../../requests/projects.request';
import type {
  InvestmentProjectsData,
  LocalizedText,
} from '../../../types/projects';

export const InvestmentProjectsSection = () => {
  const { t, i18n } = useTranslation();
  const { selectedKato } = useKato();

  const isHardKato = isProjectsDemoKato(selectedKato);

  const [data, setData] = useState<InvestmentProjectsData | null>(null);
  const [loading, setLoading] = useState(false);

  // FR-4.1: смена территории перезапрашивает реестр.
  useEffect(() => {
    if (isHardKato || selectedKato === null) return;
    let cancelled = false;
    setLoading(true);
    getProjectsInvestment(selectedKato)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        // Реста ещё нет → пустой реестр без ошибки интерфейса (FR-2.5).
        if (!cancelled) {
          console.error(
            'InvestmentProjectsSection getProjectsInvestment failed',
            err,
          );
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKato, isHardKato]);

  const projects = isHardKato
    ? (projectsInvestment as InvestmentProjectsData).projects
    : (data?.projects ?? []);

  // FR-8.1: текст из языковой версии данных; нет версии — другая её заменяет.
  const isKz = i18n.language === 'kz';
  const loc = (text: LocalizedText | null): string | null => {
    if (text === null) return null;
    return (isKz ? (text.kz ?? text.ru) : (text.ru ?? text.kz)) ?? null;
  };

  return (
    <section className="space-y-4" data-testid="projects-investment">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('projects.investment.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Landmark
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {loading ? (
        <BlockLoader />
      ) : (
        <Card className="shadow-card" data-testid="projects-investment-registry">
          <CardContent className="pt-2">
            {projects.length === 0 ? (
              // FR-2.5: пустой реестр — подпись, без ошибки.
              <p
                className="py-8 text-center text-sm text-text-muted"
                data-testid="projects-investment-empty"
              >
                {t('projects.investment.empty')}
              </p>
            ) : (
              <Accordion type="multiple">
                {projects.map((project) => {
                  // Пары «подпись: значение»; незаполненные не показываются
                  // (FR-2.2/FR-2.6). Доп. поля — следом за основными (FR-2.3).
                  const params: Array<{ label: string; value: string }> = [
                    {
                      label: t('projects.investment.params.year'),
                      value: project.year !== null ? String(project.year) : '',
                    },
                    {
                      label: t('projects.investment.params.capacity'),
                      value: loc(project.capacity) ?? '',
                    },
                    {
                      label: t('projects.investment.params.investment'),
                      value:
                        project.investmentMln !== null
                          ? fmt(project.investmentMln)
                          : '',
                    },
                    {
                      label: t('projects.investment.params.jobs'),
                      value: project.jobs !== null ? fmt(project.jobs) : '',
                    },
                    {
                      label: t('projects.investment.params.description'),
                      value: loc(project.description) ?? '',
                    },
                    {
                      label: t('projects.investment.params.effect'),
                      value: loc(project.effect) ?? '',
                    },
                    ...project.extraFields.flatMap((field) => {
                      const label = loc(field.label);
                      const value = loc(field.value);
                      return label !== null && value !== null
                        ? [{ label, value }]
                        : [];
                    }),
                  ].filter((param) => param.value !== '');

                  return (
                    <AccordionItem
                      key={project.id}
                      value={String(project.id)}
                      className="border-surface-border last:border-b-0"
                    >
                      <AccordionTrigger
                        className="gap-3 text-left text-sm font-semibold hover:no-underline"
                        data-testid={`projects-investment-row-${project.id}`}
                      >
                        {loc(project.name)}
                      </AccordionTrigger>
                      <AccordionContent
                        data-testid={`projects-investment-details-${project.id}`}
                      >
                        <dl className="space-y-2.5">
                          {params.map((param) => (
                            <div
                              key={param.label}
                              className="flex flex-col gap-0.5 sm:flex-row sm:gap-3"
                            >
                              <dt className="shrink-0 text-text-faint sm:w-64">
                                {param.label}
                              </dt>
                              <dd className="min-w-0 font-medium">
                                {param.value}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {project.photos.length > 0 && (
                          <PhotoGallery
                            photos={project.photos}
                            title={t('projects.investment.photos')}
                            testId={`projects-investment-photos-${project.id}`}
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  );
};
